import { supabase } from '../../supabase';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RecurringShift } from '@shiftly/shared';
import { DateTime } from 'luxon';

const SupabaseFixedShiftService = {
  async getFixedShiftsByModel(model_id: string): Promise<RecurringShift[]> {
    try {
      const { data, error } = await supabase
        .from('fixed_shifts')
        .select('*')
        .eq('model_id', model_id);

      if (error) {
        console.error('Error fetching fixed shifts:', error);
        throw error;
      }

      return (data || []);
    } catch (error) {
      console.error('Error in getFixedShiftsByModel:', error);
      return [];
    }
  },

  async createFixedShift(shift: Omit<RecurringShift, 'id' | 'created_at' | 'updated_at'>): Promise<string> {
    try {
      const now = DateTime.utc();
      const { data, error } = await supabase
        .from('fixed_shifts')
        .insert({
          model_id: shift.model_id,
          user_id: shift.user_id || null,
          day_of_week: shift.day_of_week,
          start_time: shift.start_time,
          end_time: shift.end_time,
          shift_code: shift.shift_code,
          created_at: now.toISO(),
          updated_at: now.toISO()
        })
        .select('id')
        .single();

      if (error) {
        console.error('Error creating fixed shift:', error);
        throw error;
      }

      return data.id;
    } catch (error) {
      console.error('Error in createFixedShift:', error);
      throw error;
    }
  },

  async deleteFixedShift(shift_id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('fixed_shifts')
        .delete()
        .eq('id', shift_id);

      if (error) {
        console.error('Error deleting fixed shift:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in deleteFixedShift:', error);
      throw error;
    }
  },

  async updateFixedShift(shift_id: string, data: Partial<RecurringShift>): Promise<void> {
    try {
      const updated_ata: any = {
        updated_at: DateTime.utc().toISO()
      };

      if (data.model_id !== undefined) updated_ata.model_id = data.model_id;
      if (data.user_id !== undefined) updated_ata.user_id = data.user_id;
      if (data.day_of_week !== undefined) updated_ata.day_of_week = data.day_of_week;
      if (data.start_time !== undefined) updated_ata.start_time = data.start_time;
      if (data.end_time !== undefined) updated_ata.end_time = data.end_time;
      if (data.shift_code !== undefined) updated_ata.shift_code = data.shift_code;

      const { error } = await supabase
        .from('fixed_shifts')
        .update(updated_ata)
        .eq('id', shift_id);

      if (error) {
        console.error('Error updating fixed shift:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in updateFixedShift:', error);
      throw error;
    }
  },

  async importFixedShifts(source_model_id: string, target_model_id: string): Promise<void> {
    try {
      // Get all shifts from source model
      const source_shifts = await this.getFixedShiftsByModel(source_model_id);
      
      // Delete all existing shifts for target model
      const { error: delete_error } = await supabase
        .from('fixed_shifts')
        .delete()
        .eq('model_id', target_model_id);

      if (delete_error) {
        console.error('Error deleting target model shifts:', delete_error);
        throw delete_error;
      }
      
      // Create new shifts for target model
      if (source_shifts.length > 0) {
        const now = DateTime.utc();
        const inserts_data = source_shifts.map(shift => ({
          model_id: target_model_id,
          user_id: shift.user_id,
          day_of_week: shift.day_of_week,
          start_time: shift.start_time,
          end_time: shift.end_time,
          shift_code: shift.shift_code,
          created_at: now.toISO(),
          updated_at: now.toISO()
        }));

        const { error: insert_error } = await supabase
          .from('fixed_shifts')
          .insert(inserts_data);

        if (insert_error) {
          console.error('Error inserting new shifts:', insert_error);
          throw insert_error;
        }
      }
    } catch (error) {
      console.error('Error in importFixedShifts:', error);
      throw error;
    }
  },

  async clearFixedShifts(model_id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('fixed_shifts')
        .delete()
        .eq('model_id', model_id);

      if (error) {
        console.error('Error clearing fixed shifts:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in clearFixedShifts:', error);
      throw error;
    }
  },

  async getFixedShiftsByModels(model_ids: string[]): Promise<RecurringShift[]> {
    try {
      if (!model_ids.length) return [];
      
      // Split model_ids into chunks
      const chunk_size = 100;
      const model_id_chunks = Array.from({ length: Math.ceil(model_ids.length / chunk_size) }, (_, i) =>
        model_ids.slice(i * chunk_size, (i + 1) * chunk_size)
      );

      const query_promises = model_id_chunks.map(async chunk => {
        const { data, error } = await supabase
          .from('fixed_shifts')
          .select('*')
          .in('model_id', chunk);

        if (error) {
          console.error('Error fetching fixed shifts chunk:', error);
          throw error;
        }

        return (data || []);
      });

      const results = await Promise.all(query_promises);
      return results.flat();
    } catch (error) {
      console.error('Error in getFixedShiftsByModels:', error);
      return [];
    }
  },

  async getFixedShiftsByUserId(user_id: string): Promise<RecurringShift[]> {
    try {
      const { data, error } = await supabase
        .from('fixed_shifts')
        .select('*')
        .eq('user_id', user_id);

      if (error) {
        console.error('Error fetching fixed shifts by user:', error);
        throw error;
      }

      return (data || []);
    } catch (error) {
      console.error('Error in getFixedShiftsByuser_id:', error);
      return [];
    }
  },

  async analyzeShiftGaps(params?: { pay_class: string }) {
    try {
      const { data, error } = await supabase.functions.invoke('shifts-sales-gap-analyzer', {
        body: params || {}
      });

      if (error) {
        console.error('Error analyzing shift gaps:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error analyzing shift gaps:', error);
      throw error;
    }
  },

  async generateShiftsForModelBetweenDates(model_id: string, start_date: DateTime, end_date: DateTime) {
    try {
      const { data, error } = await supabase.functions.invoke('shifts-sales-scheduled-generation', {
        body: {
          trigger: 'date_range',
          model_id: model_id,
          start_date: start_date.toISO(),
          end_date: end_date.toISO()
        }
      });

      if (error) {
        console.error('Error generating shifts for model between dates:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error generating shifts for model between dates:', error);
      throw error;
    }
  }
};

// TanStack Query Hooks
export const useModelFixedShifts = (model_id: string) => {
  return useQuery({
    queryKey: ['supabase-fixed-shifts', 'model', model_id],
    queryFn: () => SupabaseFixedShiftService.getFixedShiftsByModel(model_id),
    enabled: !!model_id,
  });
};

export const useCreateFixedShift = () => {
  const query_client = useQueryClient();
  
  return useMutation({
    mutationFn: SupabaseFixedShiftService.createFixedShift,
    onSuccess: (_, variables) => {
      query_client.invalidateQueries({ 
        queryKey: ['supabase-fixed-shifts', 'model', variables.model_id] 
      });
    },
  });
};

export const useDeleteFixedShift = (model_id: string) => {
  const query_client = useQueryClient();
  
  return useMutation({
    mutationFn: SupabaseFixedShiftService.deleteFixedShift,
    onSuccess: () => {
      query_client.invalidateQueries({ 
        queryKey: ['supabase-fixed-shifts', 'model', model_id] 
      });
    },
  });
};

export const useUpdateFixedShift = (model_id: string) => {
  const query_client = useQueryClient();
  
  return useMutation({
    mutationFn: ({ shift_id, data }: { shift_id: string, data: Partial<RecurringShift> }) => 
      SupabaseFixedShiftService.updateFixedShift(shift_id, data),
    onSuccess: () => {
      query_client.invalidateQueries({ 
        queryKey: ['supabase-fixed-shifts', 'model', model_id] 
      });
    },
  });
};

export const useImportFixedShifts = (model_id: string) => {
  const query_client = useQueryClient();
  
  return useMutation({
    mutationFn: (source_model_id: string) => 
      SupabaseFixedShiftService.importFixedShifts(source_model_id, model_id),
    onSuccess: () => {
      query_client.invalidateQueries({ 
        queryKey: ['supabase-fixed-shifts', 'model', model_id] 
      });
    },
  });
};

export const useClearFixedShifts = (model_id: string) => {
  const query_client = useQueryClient();
  
  return useMutation({
    mutationFn: () => SupabaseFixedShiftService.clearFixedShifts(model_id),
    onSuccess: () => {
      query_client.invalidateQueries({ 
        queryKey: ['supabase-fixed-shifts', 'model', model_id] 
      });
    },
  });
};

export const useModelsFixedShifts = (model_ids: string[]) => {
  return useQuery({
    queryKey: ['supabase-fixed-shifts', 'models', model_ids],
    queryFn: () => SupabaseFixedShiftService.getFixedShiftsByModels(model_ids),
    enabled: !!model_ids.length,
  });
};

export const useMultipleUsersFixedShifts = (user_ids: string[]) => {
  return useQuery({
    queryKey: ['supabase-fixed-shifts', 'multipleUsers', user_ids],
    queryFn: async () => {
      if (!user_ids.length) return {};
      
      const results = await Promise.all(
        user_ids.map(async (user_id) => {
          const shifts = await SupabaseFixedShiftService.getFixedShiftsByUserId(user_id);
          return { user_id, shifts };
        })
      );
      
      // Convert to object for easy lookup
      return results.reduce((acc, { user_id, shifts }) => {
        acc[user_id] = shifts;
        return acc;
      }, {} as { [user_id: string]: RecurringShift[] });
    },
    enabled: user_ids.length > 0,
  });
};

export const useAnalyzeShiftGaps = () => {
  return useMutation({
    mutationFn: (params?: { pay_class: string }) => SupabaseFixedShiftService.analyzeShiftGaps(params)
  });
};

export const useGenerateShiftsForModelBetweenDates = (model_id: string, start_date: DateTime, end_date: DateTime) => {
  return useMutation({
    mutationFn: () => SupabaseFixedShiftService.generateShiftsForModelBetweenDates(model_id, start_date, end_date),
  });
};

export default SupabaseFixedShiftService;